"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { Sparkles } from "lucide-react";

import ProductImage from "@/components/product/ProductImage";
import { MobileModalScaffold } from "@/components/shared/MobileModalScaffold";
import { SheetModalShell } from "@/components/shared/SheetModalShell";
import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input"; // Not used
import { useGlobalAssistantSuppression } from "@/hooks/useGlobalAssistantSuppression";
import {
  getFormattedPrice,
  getProductName,
  parsePriceNumber,
  type Product,
} from "@/lib/product-utils";
import { AGE_GROUPS_BY_DEPTH0, AGE_GROUPS_BY_NAVBAR_CATEGORY } from "@/lib/shop-agent-client";
import { normalizeSignal, shouldShowAttributeForContext } from "@/utils/attributeVisibility";
import { callerFacet, clientGeneratedFacet, withCallerValues } from "@/lib/facet-provenance";

export interface MoreLikeThisSearchData {
  /** The product the search is like: the more-like-this source. */
  sourceUuid?: string;
  styles: string[];
  tags: string[];
  facets: Array<{ name: string; values: string[] }>;
  exclusionFacets: Array<{ name: string; values: string[] }>;
  freeformInput?: string;
  pricePreference?: "lower" | "any" | "higher";
  productPrice?: number;
}

interface MoreLikeThisModalProps {
  product: Product;
  onClose: () => void;
  onSubmit: (data: MoreLikeThisSearchData) => void;
  showPricePreference?: boolean;
}

type FilterState = "exclude" | "indifferent" | "include";
type PricePreference = "lower" | "any" | "higher";

type PreferenceVariant = "mobile" | "desktop";
const PREF_ID_SEPARATOR = "::";

type StructuredAttributeValue = {
  name: string;
  handle: string;
};

type StructuredAttribute = {
  name?: string | null;
  handle?: string | null;
  values?: StructuredAttributeValue[] | null;
};

type ProductEnrichmentForMlt = {
  age_groups?: string[] | null;
  styles?: string[] | null;
  tags?: string[] | null;
  category_path?: string[] | null;
  structured_attributes?: StructuredAttribute[] | null;
  attribute_handles?: Record<string, string[]> | null;
  attributes?: Record<string, string[]> | null;
};

type ProductCategoryForMlt = {
  name?: string | null;
};

const AGE_GROUP_ATTRIBUTE_NAMES = new Set(["age_group", "age_groups"]);
const NORMALIZED_AGE_GROUPS_BY_DEPTH0: Record<string, string[]> = Object.entries(
  AGE_GROUPS_BY_DEPTH0
).reduce<Record<string, string[]>>((acc, [key, ageGroups]) => {
  if (Array.isArray(ageGroups)) {
    acc[normalizeSignal(key)] = ageGroups;
  }
  return acc;
}, {});
const NORMALIZED_AGE_GROUPS_BY_NAVBAR_CATEGORY: Record<string, string[]> = Object.fromEntries(
  Object.entries(AGE_GROUPS_BY_NAVBAR_CATEGORY).map(([key, ageGroups]) => [
    normalizeSignal(key),
    ageGroups,
  ])
);

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const unique = new Set<string>();
  values.forEach((value) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (!trimmed) return;
    unique.add(trimmed);
  });
  return Array.from(unique);
}

function formatHandleForDisplay(value: string): string {
  return value.replace(/__/g, " ").replace(/_/g, " ");
}

function PreferenceOptionRow({
  optionId,
  label,
  state,
  onToggleExclude,
  onSetNeutral,
  onToggleInclude,
  disabled,
  variant,
  getFilterButtonClass,
}: {
  optionId: string;
  label: string;
  state: FilterState;
  onToggleExclude: (optionId: string) => void;
  onSetNeutral: (optionId: string) => void;
  onToggleInclude: (optionId: string) => void;
  disabled: boolean;
  variant: PreferenceVariant;
  getFilterButtonClass: (state: FilterState) => string;
}) {
  const gridClassName = variant === "mobile" ? "w-28" : "w-40";
  const buttonClassName = variant === "mobile" ? "h-8 w-8 text-sm" : "h-7 w-7 text-xs";
  const labelClassName =
    variant === "mobile"
      ? "min-w-0 flex-1 truncate text-sm text-gray-700"
      : "text-sm font-normal text-gray-700";
  const dotClassName = variant === "mobile" ? "h-1.5 w-1.5" : "h-1 w-1";

  return (
    <div className={`flex items-center justify-between ${variant === "mobile" ? "gap-3" : ""}`}>
      <span className={labelClassName}>{label}</span>
      <div className={`grid ${gridClassName} grid-cols-3 place-items-center`}>
        <button
          type="button"
          onClick={() => onToggleExclude(optionId)}
          className={`flex ${buttonClassName} items-center justify-center rounded-full border transition-all duration-200 ${
            state === "exclude"
              ? getFilterButtonClass("exclude")
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          }`}
          disabled={disabled}
          aria-label="Exclude"
          title="Exclude"
        >
          −
        </button>
        <button
          type="button"
          onClick={() => onSetNeutral(optionId)}
          className={`flex ${buttonClassName} items-center justify-center rounded-full border transition-all duration-200 ${
            state === "indifferent"
              ? getFilterButtonClass("indifferent")
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          }`}
          disabled={disabled}
          aria-label="Neutral"
          title="Neutral"
        >
          <span
            className={`block ${dotClassName} rounded-full ${
              state === "indifferent" ? "bg-gray-800" : "bg-gray-400"
            }`}
          />
        </button>
        <button
          type="button"
          onClick={() => onToggleInclude(optionId)}
          className={`flex ${buttonClassName} items-center justify-center rounded-full border transition-all duration-200 ${
            state === "include"
              ? getFilterButtonClass("include")
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          }`}
          disabled={disabled}
          aria-label="Require"
          title="Require"
        >
          +
        </button>
      </div>
    </div>
  );
}

export function MoreLikeThisModal({
  product,
  onClose,
  onSubmit,
  showPricePreference = true,
}: MoreLikeThisModalProps) {
  const [isOpen, setIsOpen] = useState(true);
  const [pricePreference, setPricePreference] = useState<PricePreference>("any");
  const [stylePreferences, setStylePreferences] = useState<Record<string, FilterState>>({});
  const [showAllPreferences, setShowAllPreferences] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useGlobalAssistantSuppression(isOpen);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("moreLikeThisModalVisibility", { detail: { isOpen } }));
  }, [isOpen]);

  const getFilterButtonClass = (state: FilterState) => {
    switch (state) {
      case "exclude":
        return "border-red-500 text-red-600 bg-white hover:bg-red-50";
      case "include":
        return "border-gray-900 bg-gray-900 text-white hover:bg-gray-800";
      default:
        return "border-gray-300 text-gray-600 bg-white hover:bg-gray-50";
    }
  };

  const getPriceButtonClass = (isSelected: boolean) => {
    return isSelected
      ? "border-gray-900 bg-gray-900 text-white hover:bg-gray-800"
      : "border-gray-300 text-gray-600 bg-white hover:bg-gray-50";
  };

  // Build grouped style preference options from product enrichment attributes
  const attributeGroups = useMemo(() => {
    const enrichment = (
      product as Product & {
        enrichment?: ProductEnrichmentForMlt;
        audience?: { age_groups?: string[] | null };
        categories?: Array<ProductCategoryForMlt> | null;
      }
    )?.enrichment;

    const groups: { title: string; items: { id: string; value: string }[] }[] = [];
    const enrichmentAgeGroups = Array.isArray(enrichment?.age_groups) ? enrichment.age_groups : [];
    const audienceAgeGroups = Array.isArray(
      (
        product as Product & {
          audience?: { age_groups?: string[] | null };
        }
      )?.audience?.age_groups
    )
      ? (
          product as Product & {
            audience?: { age_groups?: string[] | null };
          }
        ).audience?.age_groups || []
      : [];
    const categoryPathSignals = Array.isArray(enrichment?.category_path)
      ? enrichment.category_path
      : [];
    const productCategorySignals = (
      (
        product as Product & {
          categories?: Array<ProductCategoryForMlt> | null;
        }
      )?.categories || []
    )
      .map((category) => (typeof category?.name === "string" ? category.name : ""))
      .filter((name) => Boolean(name && name.trim().length > 0));
    const tagSignals = Array.isArray(enrichment?.tags) ? enrichment.tags : [];
    const styleSignals = Array.isArray(enrichment?.styles) ? enrichment.styles : [];
    const legacyAttributes: Record<string, string[]> =
      enrichment?.attribute_handles || enrichment?.attributes || {};
    const legacyAttributeSignals = Object.entries(legacyAttributes).flatMap(([name, values]) => [
      name,
      ...(Array.isArray(values) ? values : []),
    ]);

    // Preferred source: structured_attributes with backend-safe handles.
    const structuredAttributes = enrichment?.structured_attributes;
    const structuredAttributeSignals = Array.isArray(structuredAttributes)
      ? structuredAttributes.flatMap((attribute) => [
          attribute?.name || "",
          attribute?.handle || "",
          ...((attribute?.values || []).flatMap((value) => [
            value?.name || "",
            value?.handle || "",
          ]) || []),
        ])
      : [];
    const inferredAgeGroupsFromLegacyAttributes = Object.entries(legacyAttributes).flatMap(
      ([attributeName, values]) => {
        const normalizedName = normalizeSignal(attributeName);
        if (!AGE_GROUP_ATTRIBUTE_NAMES.has(normalizedName)) return [];
        return Array.isArray(values) ? values : [];
      }
    );
    const inferredAgeGroupsFromStructuredAttributes = Array.isArray(structuredAttributes)
      ? structuredAttributes.flatMap((attribute) => {
          const attributeKey = normalizeSignal(attribute?.handle || attribute?.name || "");
          if (!AGE_GROUP_ATTRIBUTE_NAMES.has(attributeKey)) return [];
          return (attribute?.values || []).flatMap((value) => [
            value?.handle || "",
            value?.name || "",
          ]);
        })
      : [];
    const inferredAgeGroupsFromCategoryPath = (() => {
      if (categoryPathSignals.length === 0) return [];
      const depth0 = normalizeSignal(categoryPathSignals[0]);
      return (
        NORMALIZED_AGE_GROUPS_BY_DEPTH0[depth0] ||
        NORMALIZED_AGE_GROUPS_BY_NAVBAR_CATEGORY[depth0] ||
        []
      );
    })();
    const effectiveAgeGroups = uniqueNonEmpty([
      ...enrichmentAgeGroups,
      ...audienceAgeGroups,
      ...inferredAgeGroupsFromLegacyAttributes,
      ...inferredAgeGroupsFromStructuredAttributes,
      ...inferredAgeGroupsFromCategoryPath,
    ]);
    const visibilityContext = {
      categoryPath: [...categoryPathSignals, ...productCategorySignals],
      ageGroups: effectiveAgeGroups,
      additionalSignals: [
        ...tagSignals,
        ...styleSignals,
        ...legacyAttributeSignals,
        ...structuredAttributeSignals,
      ],
    };

    if (Array.isArray(structuredAttributes) && structuredAttributes.length > 0) {
      structuredAttributes.forEach((attribute) => {
        const attributeHandle = (attribute.handle || "").trim();
        if (!attributeHandle) return;
        if (!shouldShowAttributeForContext(attributeHandle, visibilityContext)) return;

        const items = (attribute.values || [])
          .filter((value): value is StructuredAttributeValue =>
            Boolean(value && typeof value.handle === "string" && value.handle.trim().length > 0)
          )
          .map((value) => {
            const valueHandle = (value.handle || "").trim();
            const valueName = (value.name || "").trim();
            return {
              id: `${attributeHandle}${PREF_ID_SEPARATOR}${valueHandle}`,
              value: valueName || formatHandleForDisplay(valueHandle),
            };
          });

        if (items.length > 0) {
          const title = (attribute.name || "").trim() || formatHandleForDisplay(attributeHandle);
          groups.push({ title, items });
        }
      });

      return groups;
    }

    // Backward compatibility: fallback to legacy enrichment.attributes if structured_attributes
    // is not present yet for the product.
    Object.entries(legacyAttributes).forEach(([attrKey, values]) => {
      if (!shouldShowAttributeForContext(attrKey, visibilityContext)) return;
      if (!Array.isArray(values)) return;
      const items = values
        .filter((v): v is string => typeof v === "string" && v.trim().length > 0)
        .map((v) => ({ id: `${attrKey}${PREF_ID_SEPARATOR}${v}`, value: v }));
      if (items.length > 0) {
        groups.push({ title: formatHandleForDisplay(attrKey), items });
      }
    });

    return groups;
  }, [product]);

  const togglePreference = useCallback((optionId: string, target: "exclude" | "include") => {
    setStylePreferences((prev) => {
      const current = prev[optionId] || "indifferent";
      const next: FilterState = current === target ? "indifferent" : target;
      return { ...prev, [optionId]: next };
    });
  }, []);

  const setPreferenceNeutral = useCallback((optionId: string) => {
    setStylePreferences((prev) => ({ ...prev, [optionId]: "indifferent" }));
  }, []);

  const handleApply = useCallback(async () => {
    setIsLoading(true);

    try {
      const productEnrichment = (
        product as Product & {
          enrichment?: ProductEnrichmentForMlt;
        }
      )?.enrichment;

      // Get styles and tags directly from enrichment (not from attributes)
      const productStyles = productEnrichment?.styles || [];
      const productTags = productEnrichment?.tags || [];

      // Build facets array
      const facets: Array<{ name: string; values: string[] }> = [];

      // 1. Add category facets from product enrichment
      // ProductEnrichment has category_path: Array<string> with the full taxonomy path
      const categoryPath = productEnrichment?.category_path;
      if (categoryPath && Array.isArray(categoryPath) && categoryPath.length > 0) {
        // Build facets for each depth level
        // Derived from the product, not chosen by the shopper on this search:
        // a client inference, droppable like the server's own.
        categoryPath.forEach((category, index) => {
          facets.push(
            clientGeneratedFacet(`category_path.depth_${index}`, [category.toLowerCase()])
          );
        });
      }

      // 2. Add facets and exclusion facets based on user's style preferences from the modal
      const exclusionFacets: Array<{ name: string; values: string[] }> = [];
      const selectedPreferences = Object.entries(stylePreferences).filter(
        ([, state]) => state !== "indifferent"
      );
      for (const [prefId, state] of selectedPreferences) {
        const separatorIndex = prefId.indexOf(PREF_ID_SEPARATOR);
        if (separatorIndex <= 0) continue;

        // Option IDs are persisted as "attributeHandle::valueHandle".
        const attribute = prefId.slice(0, separatorIndex).trim();
        const value = prefId.slice(separatorIndex + PREF_ID_SEPARATOR.length).trim();
        if (!attribute || !value) continue;

        const targetList = state === "include" ? facets : exclusionFacets;
        // Explicit include/exclude preferences are the shopper's: stamped so
        // relaxation never drops them.
        const existingIdx = targetList.findIndex((f) => f.name === attribute);
        if (existingIdx >= 0) {
          const existingFacet = targetList[existingIdx];
          if (!existingFacet.values.includes(value)) {
            targetList[existingIdx] = withCallerValues(existingFacet, [
              ...existingFacet.values,
              value,
            ]);
          }
        } else {
          targetList.push(callerFacet(attribute, [value]));
        }
      }

      // Extract numeric price for price preference translation
      const numericPrice = parsePriceNumber(product.current_price);

      const searchData: MoreLikeThisSearchData = {
        sourceUuid: product.uuid ?? undefined,
        styles: Array.isArray(productStyles) ? productStyles : [],
        tags: Array.isArray(productTags) ? productTags : [],
        facets,
        exclusionFacets,
        pricePreference: showPricePreference ? pricePreference : undefined,
        productPrice: numericPrice ?? undefined,
      };

      onSubmit(searchData);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  }, [handleClose, onSubmit, pricePreference, product, showPricePreference, stylePreferences]);

  // no-op: filter pill helper removed with Product Tags section

  const mobileContent = useMemo(() => {
    if (!product) return undefined;
    const price = getFormattedPrice(product);

    return {
      productImage: (
        <div className="flex flex-col items-center">
          {/* Header (keep styling consistent with other Cosimo panels) */}
          <div className="mb-4 w-full text-center">
            <p className="text-xs font-medium uppercase tracking-widest text-gray-500">
              More Like This
            </p>
            <p className="mt-1 text-sm text-gray-600">
              Refine your preferences to get better recommendations
            </p>
          </div>

          {/* Product Reference Card */}
          <div className="mb-4 w-full max-w-xs rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="relative h-20 w-16 flex-shrink-0 overflow-hidden rounded-lg bg-gray-50">
                <ProductImage
                  product={product}
                  alt={getProductName(product)}
                  objectFit="cover"
                  priority={false}
                  loading="lazy"
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-xs font-normal uppercase tracking-widest text-gray-500">
                  {product.enrichment?.canonical_brand?.brand_name ||
                    product.brand_name ||
                    product.catalog ||
                    "Brand"}
                </p>
                <h3 className="mb-1 truncate font-medium leading-tight text-gray-900">
                  {getProductName(product)}
                </h3>
                <p className="text-sm text-gray-600">{price.final}</p>
              </div>
            </div>
          </div>
        </div>
      ),
      sections: [
        {
          id: "preferences",
          title: "Style Preferences",
          defaultOpen: true,
          showDivider: false,
          content: (
            <div className="space-y-4">
              <div className="flex items-center justify-end">
                <div className="grid w-28 grid-cols-3 place-items-center text-[10px] uppercase tracking-wider text-gray-400">
                  <span className="text-center">−</span>
                  <span className="text-center">•</span>
                  <span className="text-center">+</span>
                </div>
              </div>

              <div className="space-y-4">
                {attributeGroups.slice(0, showAllPreferences ? undefined : 3).map((group) => (
                  <div key={group.title} className="space-y-2">
                    <h5 className="text-xs uppercase tracking-widest text-gray-500">
                      {group.title}
                    </h5>
                    <div className="space-y-2">
                      {group.items.map((option) => (
                        <PreferenceOptionRow
                          key={option.id}
                          optionId={option.id}
                          label={option.value}
                          state={stylePreferences[option.id] || "indifferent"}
                          onToggleExclude={(id) => togglePreference(id, "exclude")}
                          onSetNeutral={setPreferenceNeutral}
                          onToggleInclude={(id) => togglePreference(id, "include")}
                          disabled={isLoading}
                          variant="mobile"
                          getFilterButtonClass={getFilterButtonClass}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {!showAllPreferences && attributeGroups.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllPreferences(true)}
                    className="text-sm font-normal text-gray-500 underline-offset-2 transition-colors hover:text-gray-700 hover:underline"
                    disabled={isLoading}
                  >
                    Load More
                  </button>
                )}
              </div>
            </div>
          ),
        },
        ...(showPricePreference
          ? [
              {
                id: "price",
                title: "Price Preference",
                defaultOpen: true,
                showDivider: true,
                content: (
                  <div className="grid grid-cols-3 gap-2">
                    {(["lower", "any", "higher"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setPricePreference(option)}
                        className={`rounded-lg border px-4 py-2 text-sm transition-all duration-200 ${getPriceButtonClass(pricePreference === option)}`}
                        disabled={isLoading}
                      >
                        {option === "lower" ? "Lower" : option === "any" ? "Any" : "Higher"}
                      </button>
                    ))}
                  </div>
                ),
              },
            ]
          : []),
      ],
      primaryButton: {
        text: isLoading ? "Finding Similar..." : "See Similar",
        onClick: handleApply,
        disabled: isLoading,
        loading: isLoading,
      },
      secondaryButton: {
        text: "Cancel",
        onClick: handleClose,
        disabled: isLoading,
      },
    };
  }, [
    attributeGroups,
    handleApply,
    handleClose,
    isLoading,
    pricePreference,
    product,
    showAllPreferences,
    showPricePreference,
    stylePreferences,
    togglePreference,
    setPreferenceNeutral,
  ]);

  return (
    <SheetModalShell
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={onClose}
      // On mobile, the inner `MobileModalScaffold` renders its own close button.
      // Avoid rendering a second, overlapping close button from the shell.
      closeButtonClassName="hidden md:flex"
      // Slightly shorter than full screen so it doesn't collide visually with the sticky header.
      sheetHeightClassName="h-[82vh]"
      // Desktop sizing parity with prior modal.
      panelClassName="md:max-h-[90vh] md:max-w-[900px] md:rounded-[8.75px]"
    >
      {/* Mobile */}
      <div className="h-full md:hidden">
        <MobileModalScaffold
          mobileContent={mobileContent}
          onClose={handleClose}
          showCloseButton
          bottomSpacerClassName="h-10"
        >
          {/* No fallback children needed; mobileContent drives this view */}
        </MobileModalScaffold>
      </div>

      {/* Desktop */}
      <div className="hidden h-full min-h-0 md:flex">
        {/* Image Section with Pure Whitespace */}
        <div className="relative flex min-h-[420px] w-[360px] flex-col items-center justify-center bg-white p-6 lg:min-h-[500px] lg:w-[420px] lg:p-12">
          {/* Clean Image Container */}
          <div className="relative h-72 w-56 overflow-hidden lg:h-96 lg:w-72">
            {product && (
              <ProductImage
                product={product}
                alt={getProductName(product)}
                objectFit="cover"
                priority={false}
                loading="lazy"
              />
            )}
          </div>

          {/* Product Subtitle */}
          {product && (
            <div className="mt-4 text-center">
              <p className="mb-1 text-xs font-normal uppercase tracking-widest text-gray-500">
                {product.enrichment?.canonical_brand?.brand_name ||
                  product.brand_name ||
                  product.catalog ||
                  "Brand"}
              </p>
              <p className="text-sm font-medium text-gray-900">{getProductName(product)}</p>
              <p className="mt-1 text-sm text-gray-600">{getFormattedPrice(product).final}</p>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="flex min-h-0 flex-1 flex-col p-8">
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto">
            {/* Product Info with Modern Layout - More space below close button */}
            <div className="space-y-4 pt-8">
              {/* Brand and Title with More Like This Icon */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs font-normal uppercase tracking-widest text-gray-500">
                    {product?.name}
                  </p>
                  <h2 className="mt-1 text-lg font-medium leading-tight text-gray-900">
                    More Like This
                  </h2>
                </div>

                {/* More Like This Icon */}
                <div className="relative ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="bg-transparent p-2 text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                    disabled
                  >
                    <Sparkles className="h-5 w-5 stroke-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Attributes and Preferences - visible by default */}
            <div className="mt-4 space-y-4">
              {/* Style Preferences */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="text-xs font-medium uppercase tracking-widest text-gray-900">
                    Style Preferences
                  </h4>
                  {/* Labels Key */}
                  <div className="flex items-center justify-between pr-2">
                    <div></div>
                    <div className="grid w-40 grid-cols-3 px-2 text-xs uppercase tracking-wider text-gray-400">
                      <span className="col-start-1 text-center">Dislike</span>
                      <span className="col-start-3 text-center">Must Have</span>
                    </div>
                  </div>
                </div>
                {/* Preferences container (no internal scroll) */}
                <div>
                  <div className="space-y-3 pr-2">
                    {attributeGroups.slice(0, showAllPreferences ? undefined : 3).map((group) => (
                      <div key={group.title} className="space-y-2">
                        <h5 className="text-xs uppercase tracking-widest text-gray-500">
                          {group.title}
                        </h5>
                        <div className="space-y-2">
                          {group.items.map((option) => (
                            <PreferenceOptionRow
                              key={option.id}
                              optionId={option.id}
                              label={option.value}
                              state={stylePreferences[option.id] || "indifferent"}
                              onToggleExclude={(id) => togglePreference(id, "exclude")}
                              onSetNeutral={setPreferenceNeutral}
                              onToggleInclude={(id) => togglePreference(id, "include")}
                              disabled={isLoading}
                              variant="desktop"
                              getFilterButtonClass={getFilterButtonClass}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Load More */}
                {!showAllPreferences && attributeGroups.length > 3 && (
                  <button
                    type="button"
                    onClick={() => setShowAllPreferences(true)}
                    className="pt-1 text-sm font-normal text-gray-500 underline-offset-2 transition-colors hover:text-gray-700 hover:underline"
                    disabled={isLoading}
                  >
                    Load More
                  </button>
                )}
              </div>

              {/* Price Preference */}
              {showPricePreference && (
                <div className="space-y-3">
                  <h4 className="text-xs font-medium uppercase tracking-widest text-gray-900">
                    Price Preference
                  </h4>
                  <div className="grid grid-cols-3 gap-2">
                    {(["lower", "any", "higher"] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setPricePreference(option)}
                        className={`rounded-lg border px-4 py-2 text-sm transition-all duration-200 ${getPriceButtonClass(pricePreference === option)}`}
                        disabled={isLoading}
                      >
                        {option === "lower" ? "Lower" : option === "any" ? "Any" : "Higher"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons (always visible) */}
          <div className="space-y-3 bg-white pt-6">
            <Button
              onClick={handleApply}
              type="button"
              className="w-full border-0 bg-gray-900 py-3 text-sm font-medium text-white shadow-none hover:bg-gray-800"
              disabled={isLoading}
            >
              {isLoading ? "Finding Similar..." : "See Similar"}
            </Button>
            <button
              type="button"
              onClick={handleClose}
              className="w-full text-sm font-normal text-gray-500 transition-colors hover:text-gray-700"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </SheetModalShell>
  );
}

export default MoreLikeThisModal;
